import { DatabasesQueryByIdsNodes } from '@tableau/data-catalog-client-components';
import { call, select } from 'redux-saga/effects';
import _ from 'underscore';

import { DataConnections } from '~src/connectivity/resources/DataConnections';

import { databasesByIds } from '../../../catalog/resources/DatabasesByIds';
import { WindowLocationService } from '../../../commonInfra/services/WindowLocationService';
import { IDataSourceDetailInfo, IDataSourceInfo } from '../../../datasource/models/IDataSourceInfo';
import { DataSources } from '../../../datasource/resources/Datasources';
import { DatasourceIdToConnectionIdsMap } from '../../../datasource/ServerApi/types/DatasourceIdToConnectionIdsMap';
import { selectItemFromHomeState } from '../../../home/selectors/HomeSelectors';
import { IError } from '../../../httpClient/serverApi/types/IError';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { MaxRequestArrayLength } from '../../../httpClient/utils/RequestHelper';
import { Projects } from '../../../projects/resources/Projects';
import { PublishedConnections } from '../../../publishedConnection/resources/PublishedConnections';
import { deepClone } from '../../../ts/helpers/DeepClone';
import { Filter } from '../../../ts/helpers/Filter';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { IDataConnectionInfo } from '../../../ts/models/IDataConnectionInfo';
import { IDataRoleInfo } from '../../../ts/models/IDataRoleInfo';
import { ILensInfo } from '../../../ts/models/ILensInfo';
import { IProjectInfo } from '../../../ts/models/IProjectInfo';
import { IPublishedConnectionInfo } from '../../../ts/models/IPublishedConnectionInfo';
import { IViewInfo } from '../../../ts/models/IViewInfo';
import { DataRoles } from '../../../ts/resources/Dataroles';
import { Lenses } from '../../../ts/resources/Lenses';
import { ExploreContentApi_ContentType } from '../../../ts/ServerApi/types/IContent';
import { Views } from '../../../views/resources/Views';
import { IWorkbookInfo } from '../../../workbook/models/IWorkbookInfo';
import { Workbooks } from '../../../workbook/resources/Workbooks';
import { makeItemsSet } from '../../ItemsSet';
import { isInCurrentSpace, selectContentFromAppState } from '../../selectors/ContentSelectors';
import { selectSpaceInfo, selectSpaceInfoItem } from '../../selectors/SpaceInfoSelectors';
import { isHomeSpace } from '../../state/SpaceInfo';

export function isValidResult(result: IResult): boolean {
  return !result || !result.errors || result.errors.length === 0;
}

export function filterOutErroredItems(errors: IError[], items: IContentInfo[]): IContentInfo[] {
  if (!errors) {
    return items;
  }

  // Create set or errors with item id as the key.
  // Then filter out those items from given list of items
  let errorIds = makeItemsSet((error: IError) => error.id , ...errors);
  return _.filter(items, (item: IContentInfo) => !errorIds.hasOwnProperty(item.id));
}

export function getWorkbookInfos(workbooks: IContentInfo[]): Promise<IWorkbookInfo[]> {
  const idArray = workbooks.map(c => c.id);
  return getContentInfos(idArray, Workbooks);
}

export function getProjectInfos(projects: IContentInfo[]): Promise<IProjectInfo[]> {
  const idArray = projects.map(c => c.id);

  return getContentInfos(idArray, Projects);
}

export function getViewInfos(views: IContentInfo[]): Promise<IViewInfo[]> {
  const idArray = views.map(c => c.id);
  return getContentInfos(idArray, Views);
}

export function getDataRoleInfos(dataRoles: IContentInfo[]): Promise<IDataRoleInfo[]> {
  const idArray = dataRoles.map(c => c.id);
  return getContentInfos(idArray, DataRoles);
}

export function getLensInfos(lens: IContentInfo[]): Promise<ILensInfo[]> {
  const idArray = lens.map(c => c.id);
  return getContentInfos(idArray, Lenses);
}

export function getPublishedConnectionInfos(publishedConnections: IContentInfo[]): Promise<IDataRoleInfo[]> {
  const idArray: string[] = publishedConnections.map(c => c.id);
  return getContentInfos(idArray, PublishedConnections);
}

export function getDatabases(databases: IContentInfo[]): Promise<DatabasesQueryByIdsNodes[]> {
  const idArray = databases.map(c => c.id);
  return getContentInfos(idArray, databasesByIds);
}

function getContentInfos<TItem extends Array<IViewInfo | IProjectInfo | IWorkbookInfo | IDataRoleInfo | ILensInfo | IPublishedConnectionInfo | DatabasesQueryByIdsNodes>>(idArray: string[], contentResource: any): Promise<TItem>{
  if(idArray.length <= MaxRequestArrayLength) {
    return getContentInfoSlice(idArray, idArray.length, contentResource);
  }

  let idx: number = 0;
  const numSlices: number = Math.ceil(idArray.length / MaxRequestArrayLength);
  let outstandingPromises: Promise<TItem>[] = [];

  for(let i = 0; i < numSlices; i++) {
    outstandingPromises.push(getContentInfoSlice(idArray.slice(idx, idx + MaxRequestArrayLength), MaxRequestArrayLength, contentResource));
    idx = idx + MaxRequestArrayLength;
  }

  return Promise.all(outstandingPromises).then((resultArray: TItem[]) => {
    return _.flatten(resultArray as any) as TItem;
  });
}

function getContentInfoSlice<TItem>(idArray: string[], numElements: number, contentResource: any): Promise<TItem>{
  return contentResource.all()
                      .addFilter(Filter.stringFieldInArray('id', idArray))
                      .getSlice(0, numElements)
                      .then((list: any) => {
                        return list.items;
                      });
}

export function getDatasourceInfos(datasources: IContentInfo[]): Promise<IDataSourceInfo[]> {
  return DataSources.fromContent(...datasources);
}

// find the matching entity item from the selected content
export function* findSelectedContent(entityId: string, entityType: ExploreContentApi_ContentType){
  const spaceInfo = yield select(selectSpaceInfo);
  if(isHomeSpace(spaceInfo)) {
    return yield select(selectItemFromHomeState, entityId, entityType);
  } else {
    const items = yield select(selectContentFromAppState);
    const item = _.find(items, (item: IContentInfo) => entityId === item.id);
    return item;
  }
}

// TODO: This is a hack and exists only because getDatasourceActions only supports data connection IDs
export function* getCorrectEntityIdForActions(entityType: ExploreContentApi_ContentType, entityId: string) {
  if (entityType === 'dataconnection') {
    const item = yield call(findSelectedContent, entityId, entityType);
    // we need the datasource id to grab the actions for a dataconnection
    return (item as IDataConnectionInfo).datasource.id;
  }
  else if (entityType === 'datasource') {
    const inCurrentSpace = yield select(isInCurrentSpace, { id: entityId, spaceType: entityType });
    const item = inCurrentSpace ?
      yield select(selectSpaceInfoItem) : yield call(findSelectedContent, entityId, entityType);

    if(DataSources.isUnifiedDatasource(item)) {
      return item.id;
    }
    const id = yield call(getDataConnectionId, entityId);
    return id || entityId;
  }

  return entityId;
}

export function* getDataConnectionId(datasourceId: string) {
  return yield getDataConnectionIds([ datasourceId ])
    .then((map: DatasourceIdToConnectionIdsMap) => map[datasourceId] ? map[datasourceId][0] : null);
}

export function getDataConnectionIds(datasourceIds: string[]): Promise<DatasourceIdToConnectionIdsMap> {
  if(datasourceIds.length > 0) {
    return DataConnections.getConnectionIdsFromDatasourceIds(datasourceIds);
  } else {
    return Promise.resolve({} as DatasourceIdToConnectionIdsMap);
  }
}

export function replaceDatasourceIdWithConnectionIdForMixedContent(contentArray: ReadonlyArray<Readonly<IContentInfo>>): Promise<IContentInfo[]> {
  const unifiedDatasources: IContentInfo[] = [];
  const contentInfoDatasources: IContentInfo[] = [];
  contentArray.forEach(function(contentElement) {
    if (contentElement.contentType === 'datasource' && !DataSources.isUnifiedDatasource(contentElement)) {
      contentInfoDatasources.push(deepClone(contentElement));
    } else {
      unifiedDatasources.push(deepClone(contentElement));
    }
  });

  return getDataConnectionIds(contentInfoDatasources.map(i => i.id))
    .then((datasourceIdToDataConnectionIdsMap: DatasourceIdToConnectionIdsMap) => {
      const contentInfoDatasourcesWithUnifiedDatasourceIds =
        contentInfoDatasources.map(contentElement => {
          const dataConnectionIds = datasourceIdToDataConnectionIdsMap[ contentElement.id ];
          return { ...contentElement, id: dataConnectionIds[ 0 ] };
        });
      return contentInfoDatasourcesWithUnifiedDatasourceIds.concat(unifiedDatasources);
    });
}

/**
 * Note: Safari and Firefox will block this as a popup by default.
 * Consider checking the Window object returned is null and using a toast to
 * prompt the user to allow popups for the site if so.
 */
export function openBlankWindow(): Window {
  return WindowLocationService.open('');
}

export function setWindowLocation(window: Window, location: string): void {
  window.location.href = location;
}

export function getDatasourceDetailInfo(datasourceId: string): Promise<IDataSourceDetailInfo> {
  return DataSources.getById(datasourceId);
}
